import { useEffect, useReducer } from "react";
import { describeError } from "../../api/client";
import type { AdminOperationalHealth, CapabilityStatus, IntegrationStatus, OperationalHealthStatus } from "../../api/types";
import { FeedbackPanel } from "../../components/FeedbackPanel";
import { useQuery } from "../../state/useQuery";
import { getOperationalHealth } from "./api";
import { guidanceCopy, healthStatusPresentation, integrationLabel, integrationStatusPresentation, metricLabelPlural } from "./copy";
import { formatVerificationLabel, healthBanner } from "./derived";

/**
 * `/configuracion` — "Estado del sistema" (REDISENO-PANEL.md §8.3,
 * panel-redesign-v3 T8). Full rewrite of the pre-redesign four-section
 * "Estado operativo" screen (Identidad/Capacidades/Integraciones/API y
 * datos, each a `DataTable`+`RecordList` pair) into the v3 design: H1 +
 * verification timestamp, a borderless status banner, one "Integraciones"
 * list (now including `admin-api`, no longer split into a separate "API y
 * datos" section), and two cards (Métricas, Cuenta). Every row is read-only
 * — both `Consulta` and `Operador` see the exact same content, so (same as
 * the pre-redesign screen) there is no write action anywhere on this page
 * and no role gate to apply.
 */
export function OperationalHealthScreen() {
  const { data, error, loading, stale, refetch } = useQuery<AdminOperationalHealth>({
    key: ["operational-health"],
    queryFn: getOperationalHealth,
  });

  // Codex finding (PR #387 review round 3): `formatVerificationLabel` reads
  // `Date.now()` only at RENDER time — if this screen stays mounted across
  // the user's local midnight with no other re-render/refetch happening in
  // between, nothing re-evaluates it, so a snapshot generated the previous
  // calendar day keeps being labeled "hoy" instead of rolling over to
  // "ayer" (or further). Scheduling one re-render at the next local
  // midnight (and rescheduling itself for the one after that) forces the
  // label to recompute at the exact moment it would otherwise go stale,
  // without needing any new data.
  const [, forceRerenderAtMidnight] = useReducer((tick: number) => tick + 1, 0);
  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;
    const scheduleNextMidnightRerender = () => {
      const now = new Date();
      const nextMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 0, 0);
      timeoutId = setTimeout(() => {
        forceRerenderAtMidnight();
        scheduleNextMidnightRerender();
      }, nextMidnight.getTime() - now.getTime());
    };
    scheduleNextMidnightRerender();
    return () => clearTimeout(timeoutId);
  }, []);

  return (
    <section aria-label="Estado del sistema" className="v3-operational-health">
      <header className="v3-operational-health__header">
        <h1>Estado del sistema</h1>
        {data && <span className="v3-operational-health__verified">{formatVerificationLabel(data.generatedAt)}</span>}
      </header>

      {loading && <p role="status">Cargando estado del sistema…</p>}

      {!data && error !== undefined && (
        <FeedbackPanel
          variant="error"
          message={describeError(error, "No se pudo cargar el estado del sistema.")}
          onRetry={refetch}
        />
      )}

      {data && (
        <>
          {stale && (
            <FeedbackPanel variant="stale" message="No se pudo actualizar." onRetry={refetch} />
          )}

          <HealthBannerSection integrations={data.integrations} identityStatus={data.identity.status} />
          <IntegrationsCard integrations={data.integrations} />

          <div className="v3-operational-health__cards">
            <MetricsCard capabilities={data.capabilities} />
            <AccountCard identity={data.identity} integrations={data.integrations} />
          </div>
        </>
      )}
    </section>
  );
}

function HealthBannerSection({
  integrations,
  identityStatus,
}: {
  integrations: IntegrationStatus[];
  /**
   * Codex review round 4 (P2, PR #387): a suspended tenant (or the identity
   * query succeeding with no row) reports this as `"degraded"` while every
   * integration stays `available` — without it, this banner had no way to
   * know the account itself needs attention and showed the green "Todo
   * funciona correctamente" copy directly above a Cuenta card already saying
   * otherwise.
   */
  identityStatus: OperationalHealthStatus;
}) {
  const banner = healthBanner(integrations, identityStatus);
  return (
    <section
      aria-label="Resumen de estado"
      role="status"
      className={`v3-operational-health__banner v3-operational-health__banner--${banner.tone}`}
    >
      <span className="v3-operational-health__banner-icon" aria-hidden="true">
        {banner.tone === "positive" ? <BannerCheckIcon /> : <BannerWarningIcon />}
      </span>
      <div className="v3-operational-health__banner-text">
        <p className="v3-operational-health__banner-title">{banner.title}</p>
        {banner.message && <p className="v3-operational-health__banner-message">{banner.message}</p>}
      </div>
    </section>
  );
}

/** Prototype warning glyph (Panel Rediseño.dc.html, Estado del sistema banner). */
function BannerWarningIcon() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 9v4M12 17h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />
    </svg>
  );
}

function BannerCheckIcon() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 12l5 5 9-10" />
    </svg>
  );
}

function IntegrationsCard({ integrations }: { integrations: IntegrationStatus[] }) {
  return (
    <section aria-label="Integraciones" className="v3-operational-health__card v3-operational-health__integrations">
      <h2>Integraciones</h2>
      <ul className="v3-operational-health__integration-list">
        {integrations.map((integration) => {
          const { label, tone } = integrationStatusPresentation(integration.status);
          return (
            <li key={integration.key} className="v3-operational-health__integration-row">
              <span className={`v3-operational-health__dot v3-operational-health__dot--${tone}`} aria-hidden="true" />
              <span className="v3-operational-health__integration-name">{integrationLabel(integration.key)}</span>
              <span className="v3-operational-health__integration-guidance">{guidanceCopy(integration.guidanceCode)}</span>
              <span className={`v3-status-pill v3-status-pill--${tone}`}>{label}</span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/**
 * adv2-slice08 (FINDING R3-002): a `degraded` capability (an actual,
 * currently-failing metric) used to fall into the SAME "Próximamente" group
 * as a merely `pending`/not-yet-implemented one — telling operators a real
 * problem was just "coming soon" instead of something needing review. Three
 * groups now, mirroring `copy.ts#integrationStatusPresentation`'s own
 * available/degraded/pending split for the Integraciones list: Disponibles
 * (green), Necesitan revisión (amber — an actual problem), Próximamente
 * (gray — genuinely not implemented yet).
 */
function MetricsCard({ capabilities }: { capabilities: CapabilityStatus[] }) {
  const available = capabilities.filter((capability) => capability.status === "available");
  const needsReview = capabilities.filter((capability) => capability.status === "degraded");
  const pending = capabilities.filter((capability) => capability.status === "pending");

  return (
    <section aria-label="Métricas" className="v3-operational-health__card v3-operational-health__panel v3-operational-health__panel--metrics">
      <h2>Métricas</h2>
      {available.length > 0 && (
        <div className="v3-operational-health__chip-group">
          <span className="v3-operational-health__chip-group-label">Disponibles</span>
          <span className="v3-operational-health__chip-row">
            {available.map((capability) => (
              <span key={capability.key} className="v3-operational-health__chip v3-operational-health__chip--on">
                ✓ {metricLabelPlural(capability.key)}
              </span>
            ))}
          </span>
        </div>
      )}
      {needsReview.length > 0 && (
        <div className="v3-operational-health__chip-group">
          <span className="v3-operational-health__chip-group-label">Necesitan revisión</span>
          <span className="v3-operational-health__chip-row">
            {needsReview.map((capability) => (
              <span key={capability.key} className="v3-operational-health__chip v3-operational-health__chip--warning">
                ⚠ {metricLabelPlural(capability.key)}
              </span>
            ))}
          </span>
        </div>
      )}
      {pending.length > 0 && (
        <div className="v3-operational-health__chip-group">
          <span className="v3-operational-health__chip-group-label">Próximamente</span>
          <span className="v3-operational-health__chip-row">
            {pending.map((capability) => (
              <span key={capability.key} className="v3-operational-health__chip v3-operational-health__chip--off">
                {metricLabelPlural(capability.key)}
              </span>
            ))}
          </span>
        </div>
      )}
    </section>
  );
}

function AccountCard({
  identity,
  integrations,
}: {
  identity: AdminOperationalHealth["identity"];
  integrations: IntegrationStatus[];
}) {
  const estado = healthStatusPresentation(identity.status);
  const panelIntegration = integrations.find((integration) => integration.key === "admin-api");
  const servicio = panelIntegration ? healthStatusPresentation(panelIntegration.status) : null;

  return (
    <section aria-label="Cuenta" className="v3-operational-health__card v3-operational-health__panel v3-operational-health__panel--account">
      <h2>Cuenta</h2>
      <dl className="v3-operational-health__account">
        <div className="v3-operational-health__account-row">
          <dt>Nombre</dt>
          <dd>{identity.displayName}</dd>
        </div>
        <div className="v3-operational-health__account-row">
          <dt>Estado</dt>
          <dd className={`v3-operational-health__account-value--${estado.tone}`}>{estado.label}</dd>
        </div>
        {servicio && (
          <div className="v3-operational-health__account-row">
            <dt>Servicio</dt>
            <dd className={`v3-operational-health__account-value--${servicio.tone}`}>{servicio.label}</dd>
          </div>
        )}
      </dl>
      <details className="v3-operational-health__technical">
        <summary>Datos técnicos para soporte</summary>
        <p className="v3-operational-health__technical-id">Tenant {identity.clientId}</p>
      </details>
    </section>
  );
}
